import type { Message } from "@ag-ui/client";
import { isHitlTool } from "./hitlTools";
import type { ToolCallEntry, Turn } from "./useTurns";

// The `tool`-role member of the AG-UI Message union (@ag-ui/client re-exports
// only Message, not ToolMessage). buildResumeToolMessages only ever returns
// these, so callers can read `toolCallId` without narrowing.
type ResumeToolMessage = Extract<Message, { role: "tool" }>;

// Un-resulted tool calls on settled (non-running, non-cancelled) assistant
// turns. The run ended without finishing its work — either a HITL pause or an
// interrupted run, which `Chat` resolves by reconciling against `/connect`.
export function danglingToolCalls(turns: readonly Turn[]): ToolCallEntry[] {
  const calls: ToolCallEntry[] = [];
  for (const turn of turns) {
    if (turn.kind !== "assistant" || turn.isRunning || turn.cancelled) continue;
    calls.push(...turn.toolCalls.filter((tc) => tc.result === undefined));
  }
  return calls;
}

// Dangling calls that are genuine HITL pauses: only the hardcoded HITL tools
// (see hitlTools.ts) can pause a run for input, so a dangling call from any
// other tool never blocks the composer or renders an answer card.
export function hitlPauseIds(calls: readonly ToolCallEntry[]): ReadonlySet<string> {
  return new Set(calls.filter((tc) => isHitlTool(tc.name)).map((tc) => tc.id));
}

// The synthetic tool-result we backfill for an open sibling call when resuming
// a HITL pause. The wording is honest: the call never produced a result before
// the run paused, so the model is told to re-plan rather than trust a fake one.
export const INTERRUPTED_TOOL_RESULT =
  "Tool call interrupted: no result was recorded before the run paused for your input.";

// The tool messages to append to the AG-UI history when the user answers a HITL
// card, in the order they should be sent.
//
// Anthropic requires every tool-use block in an assistant message to be answered
// by a matching tool-result in the following message. A paused turn can carry
// sibling tool calls that never returned — e.g. an interrupted or retried
// `execute_code` — alongside the HITL call being answered. Resuming with those
// left open makes the model request malformed: Bedrock rejects it 400 ("Expected
// toolResult blocks ... for the following Ids"), the run errors out, and the card
// never clears. So we backfill a synthetic result for every open *non-HITL*
// sibling, then append the user's real answer.
//
// Other open HITL calls are intentionally left untouched: they are the user's to
// answer via their own cards, not ours to fabricate. (Two HITL cards pending at
// once is a separate, unsupported case — answering one still leaves the other
// open.)
export function buildResumeToolMessages(
  danglingCalls: readonly ToolCallEntry[],
  answeredToolCallId: string,
  answer: string,
  newId: () => string
): ResumeToolMessage[] {
  const messages: ResumeToolMessage[] = [];
  for (const tc of danglingCalls) {
    if (tc.id === answeredToolCallId || isHitlTool(tc.name)) continue;
    messages.push({
      id: newId(),
      role: "tool",
      toolCallId: tc.id,
      content: INTERRUPTED_TOOL_RESULT,
      error: INTERRUPTED_TOOL_RESULT,
    });
  }
  messages.push({
    id: newId(),
    role: "tool",
    toolCallId: answeredToolCallId,
    content: answer,
  });
  return messages;
}
